import { useNavigate } from 'react-router-dom';

function WarrantDashboard({ data }) {
  const navigate = useNavigate();
  const teams = data?.team_overview || [];
  const attendance = data?.attendance || {};
  const sites = data?.site_overview || [];
  const incidents = data?.recent_incidents || [];

  return (
    <div className="space-y-6" data-testid="warrant-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Warrant Officer Dashboard</h1>
        <p className="text-gray-400">Team and site operations management</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Teams */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-xl font-bold text-white mb-4">My Teams</h2>
          {teams.length > 0 ? (
            <div className="space-y-3">
              {teams.map(team => (
                <div key={team.team_id} className="bg-gray-700 rounded-lg p-3">
                  <div className="flex justify-between">
                    <div>
                      <p className="text-white font-medium">{team.team_name}</p>
                      <p className="text-gray-400 text-sm">{team.site_name} - {team.member_count} members</p>
                    </div>
                    <RiskBadge risk={team.site_risk} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-4">No teams assigned</p>
          )}
        </div>

        {/* Attendance */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-xl font-bold text-white mb-4">Today&apos;s Attendance</h2>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-gray-700 rounded-lg">
              <p className="text-2xl font-bold text-white">{attendance.total_expected || 0}</p>
              <p className="text-gray-400 text-xs">Expected</p>
            </div>
            <div className="text-center p-3 bg-gray-700 rounded-lg">
              <p className="text-2xl font-bold text-green-400">{attendance.checked_in || 0}</p>
              <p className="text-gray-400 text-xs">In</p>
            </div>
            <div className="text-center p-3 bg-gray-700 rounded-lg">
              <p className="text-2xl font-bold text-blue-400">{attendance.checked_out || 0}</p>
              <p className="text-gray-400 text-xs">Out</p>
            </div>
          </div>
        </div>
      </div>

      {/* Site Oversight */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-xl font-bold text-white mb-4">Site Overview</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-gray-400 text-sm border-b border-gray-700">
                <th className="pb-3">Site</th>
                <th className="pb-3">Address</th>
                <th className="pb-3">Risk</th>
                <th className="pb-3">Teams</th>
                <th className="pb-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {sites.map(site => (
                <tr key={site.site_id} className="border-b border-gray-700">
                  <td className="py-3 text-white">{site.name}</td>
                  <td className="py-3 text-gray-400 text-sm">{site.address || '-'}</td>
                  <td className="py-3"><RiskBadge risk={site.risk_classification} /></td>
                  <td className="py-3 text-gray-300">{site.team_count}</td>
                  <td className="py-3">
                    <span className={`px-2 py-1 rounded text-xs ${
                      site.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                    }`}>{site.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button onClick={() => navigate('/deployments')} className="mt-4 px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-white text-sm">
          View Deployments
        </button>
      </div>

      {/* Recent Incidents */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-xl font-bold text-white mb-4">Recent Incidents</h2>
        {incidents.length > 0 ? (
          <div className="space-y-2">
            {incidents.slice(0, 5).map(incident => (
              <div key={incident.id} className="bg-gray-700 rounded-lg p-3 flex justify-between">
                <div>
                  <p className="text-white">{incident.incident_number}</p>
                  <p className="text-gray-400 text-sm">{incident.type}</p>
                </div>
                <span className={`px-2 py-1 h-fit rounded text-xs ${
                  incident.status === 'closed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                }`}>{incident.status}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-4">No recent incidents</p>
        )}
      </div>
    </div>
  );
}

function RiskBadge({ risk }) {
  const colors = {
    Critical: 'bg-red-500/20 text-red-400',
    High: 'bg-orange-500/20 text-orange-400',
    Medium: 'bg-yellow-500/20 text-yellow-400',
    Low: 'bg-green-500/20 text-green-400'
  };
  return (
    <span className={`px-2 py-1 rounded text-xs ${colors[risk] || colors.Low}`}>
      {risk || 'Low'}
    </span>
  );
}

export default WarrantDashboard;
